import type { BlobQueueStore } from '@jbroll/rowboat-client';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { onSynced } from '@/lib/syncSignal';
import { ANON_IDENTITY, usePort, useRowboat } from '@/rowboat';
import { createPhotoQueue, ownedQueueStore, photoQueueStore } from '@/services/photoQueue';
import {
  committed,
  drained,
  enqueued,
  failedCount,
  listed,
  pendingCount,
  type QueueState,
  started,
  type UploadStatus,
} from '@/services/uploadStatus';

interface PhotoUploads {
  enqueue: (photoId: string, bytes: Blob) => Promise<void>;
  retry: () => void;
  /** This device's queue state for a photo; undefined when nothing is queued here. */
  stateOf: (photoId: string) => QueueState | undefined;
  /** Bytes still queued on this device, for previews after a reload. */
  localBytes: (photoId: string) => Promise<Blob | undefined>;
  pending: number;
  failed: number;
  canUpload: boolean;
}

const Context = createContext<PhotoUploads | null>(null);

// One queue for the whole app, so the header counter, the inspector and the camera share
// per-photo state. Anonymous stores have no upload token, so their bytes wait for sign-up.
export function PhotoUploadsProvider({
  children,
  store: given,
}: {
  children: ReactNode;
  store?: BlobQueueStore;
}) {
  const graph = useRowboat();
  const { author, storedGroup } = usePort();
  const canUpload = author !== null;
  const owner = author ?? ANON_IDENTITY;
  const store = useMemo(
    () =>
      ownedQueueStore(given ?? photoQueueStore(), owner, (photoId) => !!graph.item_photo(photoId)),
    [given, owner, graph],
  );
  const [status, setStatus] = useState<UploadStatus>({});
  const [listedOnce, setListedOnce] = useState(false);
  const queue = useMemo(
    () =>
      createPhotoQueue(graph, {
        ownerGroupId: (photoId) => storedGroup('item_photo', photoId),
        store,
        onEntry: (photoId, event) =>
          setStatus((s) => (event === 'start' ? started(s, photoId) : committed(s, photoId))),
      }),
    [graph, store, storedGroup],
  );

  const pass = useRef(async () => {});
  pass.current = async () => {
    const keys = await store.list();
    setStatus((s) => listed(s, keys));
    setListedOnce(true);
    // Offline, a drain would only mark every entry failed; the online listener drains instead.
    if (!canUpload || navigator.onLine === false) return;
    const { failed } = await queue.drain();
    for (const { key, error } of failed) console.warn('photo upload failed', key, error);
    const failedKeys = failed.map((f) => f.key);
    setStatus((s) => drained(s, failedKeys));
  };

  // One drain at a time: overlapping drains list the same entries and upload them
  // twice. A request during a drain queues one more pass, run against the current queue.
  const running = useRef<Promise<void> | null>(null);
  const again = useRef(false);
  const drain = useCallback((): Promise<void> => {
    if (running.current) {
      again.current = true;
      return running.current;
    }
    running.current = (async () => {
      do {
        again.current = false;
        await pass.current();
      } while (again.current);
    })().finally(() => {
      running.current = null;
    });
    return running.current;
  }, []);

  // Re-run for a new queue (graph change) or when signing in makes uploads possible.
  // biome-ignore lint/correctness/useExhaustiveDependencies: queue and canUpload are triggers; drain reads them through pass
  useEffect(() => {
    void drain();
  }, [drain, queue, canUpload]);

  useEffect(() => {
    const onOnline = () => void drain();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [drain]);

  // Anonymous photos become this account's once the claim has copied their rows, which can
  // land after the mount drain.
  useEffect(
    () =>
      onSynced(() => {
        store
          .adopt()
          .then((moved) => {
            if (moved > 0) void drain();
          })
          .catch((err) => console.warn('photo outbox adopt failed', err));
      }),
    [store, drain],
  );

  // The raw store, not the queue's reporting wrapper: a preview read is not an upload start.
  const localBytes = useCallback((photoId: string) => store.get(photoId), [store]);

  const value = useMemo<PhotoUploads>(
    () => ({
      enqueue: async (photoId, bytes) => {
        await queue.enqueue(photoId, bytes);
        setStatus((s) => enqueued(s, photoId));
        void drain();
      },
      retry: () => void drain(),
      stateOf: (photoId) => status[photoId] ?? (listedOnce ? undefined : 'checking'),
      localBytes,
      pending: pendingCount(status),
      failed: failedCount(status),
      canUpload,
    }),
    [queue, drain, status, listedOnce, localBytes, canUpload],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function usePhotoUploads(): PhotoUploads {
  const value = useContext(Context);
  if (!value) throw new Error('usePhotoUploads needs a PhotoUploadsProvider');
  return value;
}

/**
 * Header counter: a fixed-width slot that is blank at zero, so the header never
 * shifts. Failed uploads turn it into a retry button.
 */
export function UploadIndicator() {
  const { pending, failed, canUpload, retry } = usePhotoUploads();
  const show = canUpload && pending > 0;
  return (
    <span className="inline-flex w-12 shrink-0 justify-center">
      {show && failed > 0 && (
        <button
          type="button"
          onClick={retry}
          aria-label={`${failed} photo uploads failed, retry`}
          title="Uploads failed, tap to retry"
          className="rounded-lg px-1.5 py-0.5 text-sm font-medium text-red-700 hover:bg-amber-100"
        >
          ↑{pending}!
        </button>
      )}
      {show && failed === 0 && (
        <output aria-label={`${pending} photos uploading`} className="text-sm text-amber-900">
          ↑{pending}
        </output>
      )}
    </span>
  );
}
